<template>
	<div class="project-grid gap-5">
        <!-- Projects Section -->
        <div class="section">
            <div class="title mb-2">
                <span>Projects</span>
            </div>
            <themed-summary
                :items="projectsSummaryItems"
                variant="compact"
                class="card"
            />
            <projects
                    :project-data="projects"
                    :show-toolbar="false"
                    :show-header="false"
                    :show-view-toggle="false"
                    :per-page="5"
                    default-view-mode="list"
                    :disable-preferences="true"
                    :columns="projectColumns"
                />
        </div>

        <!-- Sites Section -->
        <div class="section">
            <div class="title mb-2">
                <span>Sites</span>
            </div>
            <themed-summary
                :items="sitesSummaryItems"
                variant="compact"
                class="card"
            />
            <site-cards
                    :site-data="sites"
                    :show-toolbar="false"
                    :default-page-size="5"
                    default-view-mode="list"
                    :disable-preferences="true"
                    :columns="siteColumns"
                    :show-column-visibility="false"
                />
        </div>
    </div>
</template>

<script>
import ThemedSummary from '../ThemedSummary.vue';
import Projects from '../projects/Projects.vue';
import SiteCards from '../sites/SiteCards.vue';
import { formatShorthandCurrency, formatCompactNumber } from '../../../../utils/formatters';

export default {
	name: 'PropertiesTabContent',
	
	components: {
		ThemedSummary,
		Projects,
		SiteCards
	},
	
	props: {
		projects: {
			type: Array,
			required: true
		},
		sites: {
			type: Array,
			required: true
		}
	},
	
	computed: {
		totalProjects() {
			return this.projects.length;
		},
		
		totalSites() {
			return this.sites.length;
		},
		
		projectsRemaining() {
			return this.projects.reduce((sum, project) => {
				const remaining = parseFloat(project.remaining_lease_cost) || 0;
				return sum + remaining;
			}, 0);
		},
		
		projectsSqFt() {
			return this.projects.reduce((sum, project) => {
				const sqFt = parseFloat(project.current_sq_ft || project.required_sq_ft) || 0;
				return sum + sqFt;
			}, 0);
		},
		
		sitesRemaining() {
			return this.sites.reduce((sum, site) => {
				const remaining = parseFloat(site.remaining_lease_cost) || 0;
				return sum + remaining;
			}, 0);
		},
		
		sitesSqFt() {
			return this.sites.reduce((sum, site) => {
				const sqFt = parseFloat(site.size) || 0;
				return sum + sqFt;
			}, 0);
		},
		
		totalRemaining() {
			return this.projectsRemaining + this.sitesRemaining;
		},
		
		totalSqFt() {
			return this.projectsSqFt + this.sitesSqFt;
		},
		
		// Top-level summary configuration
		topSummaryItems() {
			return [
				{
					value: this.totalProjects,
					label: 'Projects'
				},
				{
					value: this.formatCurrency(this.totalRemaining),
					label: 'GM Remaining'
				},
				{
					value: this.formatNumber(this.totalSqFt),
					label: 'Total Sq. Ft.'
				}
			];
		},
		
		// Projects section summary configuration
		projectsSummaryItems() {
			return [
				{
					value: this.totalProjects,
					label: 'Projects'
				},
				{
					// remaining_lease_cost is in cents. The imported formatShorthandCurrency
					// does no conversion, unlike the global formatCurrency mixin used by
					// topSummaryItems above, so the division has to happen here.
					value: formatShorthandCurrency(this.projectsRemaining / 100),
					label: 'GM Remaining'
				},
				{
					value: formatCompactNumber(this.projectsSqFt),
					label: 'Total Sq. Ft.'
				}
			];
		},
		
		// Sites section summary configuration
		sitesSummaryItems() {
			return [
				{
					value: this.totalSites,
					label: 'Total Sites'
				}
			];
		},
		
		projectColumns() {
			return [
				{
					field: 'name',
					label: 'NAME',
					sortable: true,
					searchable: true,
					align: 'left',
				},
				{
					field: 'stage',
					label: 'STAGE',
					sortable: true,
					searchable: true,
					align: 'left',
				},
				{
					field: 'required_sq_ft',
					label: 'SQ. FT.',
					sortable: true,
					type: 'number',
					align: 'right',
					formatter: (value) => this.formatNumber(value)
				}
			];
		},
		
		siteColumns() {
			return [
				{
					field: 'building.address_line_1',
					label: 'NAME',
					type: 'text',
					sortable: true,
					showInTable: true,
					alwaysVisible: true,
					align: 'left',
				},
				{
					field: 'building.city',
					label: 'CITY',
					type: 'text',
					sortable: true,
					showInTable: true,
					align: 'left',
				},
				{
					field: 'size',
					label: 'SQ. FT.',
					type: 'number',
					sortable: true,
					showInTable: true,
					align: 'right',
					formatter: (value) => this.formatNumber(value)
				}
			];
		}
	},
	
	methods: {
		formatNumber(value) {
			return new Intl.NumberFormat('en-US', {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0
			}).format(value);
		}
	}
}
</script>
